import { useState } from "react";
import { toast } from "sonner";
import { IMAGES, SITE, buildWhatsAppUrl } from "@/lib/site";
import { Calendar, Users, Mail, User, Phone, MessageSquare, Send, MapPin, Instagram } from "lucide-react";

const initial = {
  name: "",
  phone: "",
  email: "",
  guests: "2",
  checkIn: "",
  checkOut: "",
  message: "",
};

const Booking = () => {
  const [f, setF] = useState(initial);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const buildMessage = () => {
    const lines = [
      `*New Booking Enquiry — Navilukallu Homestay*`,
      ``,
      `Name: ${f.name || "-"}`,
      `Phone: ${f.phone || "-"}`,
      `Email: ${f.email || "-"}`,
      `Guests: ${f.guests || "-"}`,
      `Check-In: ${f.checkIn || "-"}`,
      `Check-Out: ${f.checkOut || "-"}`,
      ``,
      `Message:`,
      `${f.message || "-"}`,
    ];
    return lines.join("\n");
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    const url = buildWhatsAppUrl(buildMessage());
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp with your enquiry…");
  };

  const inputBase =
    "w-full bg-white/10 backdrop-blur border border-white/20 rounded-xl px-4 py-3.5 text-white placeholder:text-white/50 focus:outline-none focus:border-[#D4AF37] focus:bg-white/15 transition-colors text-sm";

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src={IMAGES.estateHillside}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute inset-0 z-10 bg-gradient-to-br from-[#1B4332]/95 via-[#1B4332]/85 to-[#1A1A1A]/95" />

      <div className="relative z-20 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
        <div className="text-white reveal">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-[#D4AF37]" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] font-medium">
              Contact & Booking
            </span>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Reserve your{" "}
            <span className="italic font-light text-[#E8C866]">
              Malnad retreat
            </span>
          </h2>
          <p className="mt-6 text-white/75 text-base md:text-lg leading-relaxed max-w-lg">
            Send us your details and we'll respond over WhatsApp within a few
            hours — availability, tariff, and a warm welcome, guaranteed.
          </p>

          <div className="mt-10 space-y-4 max-w-md">
            <a
              href={`tel:${SITE.phoneRaw}`}
              data-testid="booking-call-link"
              className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            >
              <span className="w-11 h-11 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37]">
                <Phone size={18} strokeWidth={1.6} />
              </span>
              <div>
                <div className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37]">
                  Call
                </div>
                <div className="text-white text-base">{SITE.phoneDisplay}</div>
              </div>
            </a>
            <a
              href={buildWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-whatsapp-link"
              className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            >
              <span className="w-11 h-11 rounded-full bg-[#25D366]/15 border border-[#25D366]/30 flex items-center justify-center text-[#25D366]">
                <MessageSquare size={18} strokeWidth={1.6} />
              </span>
              <div>
                <div className="text-[11px] tracking-[0.25em] uppercase text-[#25D366]">
                  WhatsApp
                </div>
                <div className="text-white text-base">Chat with us</div>
              </div>
            </a>

            <a
              href={SITE.mapsShareUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-address-link"
              className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            >
              <span className="w-11 h-11 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37]">
                <MapPin size={18} strokeWidth={1.6} />
              </span>
              <div className="min-w-0">
                <div className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37]">
                  Address
                </div>
                <div className="text-white text-sm md:text-base leading-snug">
                  {SITE.address}
                </div>
              </div>
            </a>

            <a
              href={SITE.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-instagram-link"
              className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            >
              <span className="w-11 h-11 rounded-full bg-[#E1306C]/15 border border-[#E1306C]/30 flex items-center justify-center text-[#E1306C]">
                <Instagram size={18} strokeWidth={1.6} />
              </span>
              <div>
                <div className="text-[11px] tracking-[0.25em] uppercase text-[#E1306C]">
                  Instagram
                </div>
                <div className="text-white text-base">@navilukalluhomestay</div>
              </div>
            </a>
          </div>

          {/* Embedded Google Map */}
          <div
            data-testid="booking-map-embed"
            className="mt-6 rounded-3xl overflow-hidden border border-white/15 shadow-2xl max-w-md h-56"
          >
            <iframe
              title="Navilukallu Homestay location on Google Maps"
              src={SITE.mapsEmbedSrc}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="booking-form"
          className="reveal relative rounded-3xl p-6 sm:p-8 md:p-10 bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl"
        >
          <div className="absolute -top-3 -left-3 w-24 h-24 rounded-full bg-[#D4AF37]/10 blur-2xl" />
          <div className="absolute -bottom-3 -right-3 w-32 h-32 rounded-full bg-[#D4AF37]/15 blur-3xl" />

          <div className="relative">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-2">
              Enquire Now
            </div>
            <h3 className="font-heading text-2xl md:text-3xl text-white">
              Plan your stay
            </h3>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-white/70 flex items-center gap-2 mb-2">
                  <User size={13} /> Name
                </span>
                <input
                  data-testid="booking-input-name"
                  type="text"
                  value={f.name}
                  onChange={set("name")}
                  placeholder="Your full name"
                  className={inputBase}
                  required
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-white/70 flex items-center gap-2 mb-2">
                  <Phone size={13} /> Phone
                </span>
                <input
                  data-testid="booking-input-phone"
                  type="tel"
                  value={f.phone}
                  onChange={set("phone")}
                  placeholder="+91 ..."
                  className={inputBase}
                  required
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-[11px] uppercase tracking-wider text-white/70 flex items-center gap-2 mb-2">
                  <Mail size={13} /> Email
                </span>
                <input
                  data-testid="booking-input-email"
                  type="email"
                  value={f.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={inputBase}
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-white/70 flex items-center gap-2 mb-2">
                  <Users size={13} /> Guests
                </span>
                <input
                  data-testid="booking-input-guests"
                  type="number"
                  min="1"
                  value={f.guests}
                  onChange={set("guests")}
                  className={inputBase}
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-white/70 flex items-center gap-2 mb-2">
                  <Calendar size={13} /> Check-In
                </span>
                <input
                  data-testid="booking-input-checkin"
                  type="date"
                  value={f.checkIn}
                  onChange={set("checkIn")}
                  className={`${inputBase} [color-scheme:dark]`}
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-[11px] uppercase tracking-wider text-white/70 flex items-center gap-2 mb-2">
                  <Calendar size={13} /> Check-Out
                </span>
                <input
                  data-testid="booking-input-checkout"
                  type="date"
                  value={f.checkOut}
                  onChange={set("checkOut")}
                  className={`${inputBase} [color-scheme:dark]`}
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-[11px] uppercase tracking-wider text-white/70 flex items-center gap-2 mb-2">
                  <MessageSquare size={13} /> Message
                </span>
                <textarea
                  data-testid="booking-input-message"
                  rows="3"
                  value={f.message}
                  onChange={set("message")}
                  placeholder="Any special requests?"
                  className={inputBase + " resize-none"}
                />
              </label>
            </div>

            <button
              type="submit"
              data-testid="booking-submit-button"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 py-4 rounded-xl bg-[#D4AF37] text-[#1A1A1A] font-medium tracking-wide hover:scale-[1.01] hover:shadow-[0_0_28px_rgba(212,175,55,0.45)] transition-all duration-300"
            >
              <Send size={18} />
              Send via WhatsApp
            </button>
            <p className="mt-3 text-center text-[11px] text-white/50">
              By submitting, you'll be redirected to WhatsApp with your details
              pre-filled.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Booking;
